import * as am5 from '@amcharts/amcharts5';

import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
    Component,
    ElementRef,
    Inject,
    InputSignal,
    NgZone,
    OnDestroy,
    PLATFORM_ID,
    Signal,
    ViewChild,
    WritableSignal,
    computed,
    effect,
    inject,
    input,
    signal,
    viewChild,
} from '@angular/core';
import {
    IqEventBusBrokerHandler,
    IqEventBusBrokerService,
} from '@dat/iq-event-bus';
import { get } from 'lodash';
import z from 'zod';
import { ContractTypeEnum, Rate } from '../models';
import { IqGraphRatesStateService } from '../state';
import { GraphBuilder } from '../state/services/graph-builder.service';

const EventBusRateSchema = z.object({
    value: z.number(),
    date: z.string(),
    mileage: z.number(),
});

const RatePayloadSchema = z.object({
    id: z.string(),
    high: z.array(EventBusRateSchema),
    low: z.array(EventBusRateSchema),
    average: z.array(EventBusRateSchema),
    loaded: z.boolean(),
    errored: z.boolean(),
});

const RateTypePayloadSchema = z.object({
    id: z.string(),
    rateType: z.string(),
});

const TripTypePayloadSchema = z.object({
    id: z.string(),
    perLoad: z.boolean(),
});

const CurrentRatePayloadSchema = z.object({
    id: z.string(),
    high: z.number(),
    low: z.number(),
    average: z.number(),
    timeEsclation: z.string(),
    loaded: z.boolean(),
    errored: z.boolean(),
})

type CurrentRatePayload = z.infer<typeof CurrentRatePayloadSchema>;
type EventBusRate = z.infer<typeof EventBusRateSchema>;
type TripTypePayload = z.infer<typeof TripTypePayloadSchema>;
type RateTypePayload = z.infer<typeof RateTypePayloadSchema>;
type RatePayload = z.infer<typeof RatePayloadSchema>;

@Component({
    selector: 'iq-graph-rates-hybrid',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './iq-graph-rates-hybrid.component.html',
    styleUrl: './iq-graph-rates-hybrid.component.scss',
})
export class IqGraphRatesHybridComponent implements OnDestroy {
    id: InputSignal<string> = input('');
    vizTime: InputSignal<string> = input('');
    private _eventBus: IqEventBusBrokerService = inject(
        IqEventBusBrokerService,
    );
    graph: Signal<ElementRef> = viewChild<ElementRef>('graph');

  
    rootId = computed(() => {
        return `graph-root-hybrid-${this.id()}`;
    });
    @ViewChild('graph') container: ElementRef;

    loading: boolean = false;

    private _dispatchActions: Record<string, IqEventBusBrokerHandler> = {};

    historicalGraphData: Signal<{
        spotData: Rate[];
        contractData: Rate[];
        bestFit: { average: number; high: number; low: number };
    }> = computed(() => {
        const spotData = this.combineData(
            this.spotRatesHistorical()?.high,
            this.spotRatesHistorical()?.low,
            this.spotRatesHistorical()?.average,
        );

        const contractData = this.combineData(
            this.contractRatesHistorical()?.high,
            this.contractRatesHistorical()?.low,
            this.contractRatesHistorical()?.average,
        );

        const payloadToUse =
            get(this.rateTypePayload(), 'rateType') === 'BrokerCarrierSpot'
                ? this.spotRatesCurrent()
                : this.contractRatesCurrent();

        return {
            spotData,
            contractData,
            bestFit: {
                average: payloadToUse.average,
                high: payloadToUse.high,
                low: payloadToUse.low,
            },
        };
    });

    combineData(
        highValues: EventBusRate[],
        lowValues: EventBusRate[],
        averageValues: EventBusRate[],
    ): Rate[] {
        if (
            highValues?.length !== lowValues?.length ||
            lowValues?.length !== averageValues?.length
        ) {
            throw new Error('All arrays must have the same length');
        }
        if (!highValues) {
            return [];
        }

        return highValues.map((highValue, index) => ({
            high: highValue?.value ?? 0,
            low: lowValues[index]?.value ?? 0,
            average: averageValues[index]?.value ?? 0,
            date: highValue?.date ?? '', // Assuming all arrays have the same dates
            escalationDestination: '',
            escalationOrigin: '',
        }));
    }

    spotRatesCurrent: WritableSignal<CurrentRatePayload> = signal({});
    contractRatesCurrent: WritableSignal<CurrentRatePayload> = signal({});
    spotRatesHistorical: WritableSignal<RatePayload> = signal({});
    contractRatesHistorical: WritableSignal<RatePayload> = signal({});
    spotRates13MForecast: WritableSignal<RatePayload> = signal({});
    contractRates13MForecast: WritableSignal<RatePayload> = signal({});
    spotRates35DForecast: WritableSignal<RatePayload> = signal({});
    contractRates35DForecast: WritableSignal<RatePayload> = signal({});
    rateTypePayload: WritableSignal<RateTypePayload> = signal({});
    tripTypePayload: WritableSignal<TripTypePayload> = signal({});
    constructor(
        private readonly _state: IqGraphRatesStateService,
        @Inject(PLATFORM_ID) private platformId,
        private zone: NgZone,
        private _graphBuilder: GraphBuilder,
    ) {
        const handlerOpts = {
            parentId: this.id(),
        };

        this._dispatchActions = this._eventBus.registerMap({
            setRateGraphSpotRatesCurrent: {
                callback: (payload: CurrentRatePayload) => {
                    if (this.id() !== payload.id) return;
                    this.spotRatesCurrent.set(payload);
                },
                ...handlerOpts,
            },
            setRateGraphContractRatesCurrent: {
                callback: (payload: CurrentRatePayload) => {
                    if (this.id() !== payload.id) return;
                    this.contractRatesCurrent.set(payload);
                },
                ...handlerOpts,
            },
            setRateGraphSpotRatesHistorical: {
                callback: (payload: RatePayload) => {
                    if (this.id() !== payload.id) return;
                    this.spotRatesHistorical.set(payload);
                },
                ...handlerOpts,
            },
            setRateGraphContractRatesHistorical: {
                callback: (payload: RatePayload) => {
                    if (this.id() !== payload.id) return;

                    this.contractRatesHistorical.set(payload);
                },
                ...handlerOpts,
            },
            setRateGraphSpotRates13MForecast: {
                callback: (payload: RatePayload) => {
                    if (this.id() !== payload.id) return;

                    this.spotRates13MForecast.set(payload);
                },
                ...handlerOpts,
            },
            setRateGraphContractRates13MForecast: {
                callback: (payload: RatePayload) => {
                    if (this.id() !== payload.id) return;

                    this.contractRates13MForecast.set(payload);
                },
                ...handlerOpts,
            },
            setRateGraphSpotRates35DForecast: {
                callback: (payload: RatePayload) => {
                    if (this.id() !== payload.id) return;
                    this.spotRates35DForecast.set(payload);
                },
                ...handlerOpts,
            },
            setRateGraphContractRates35DForecast: {
                callback: (payload: RatePayload) => {
                    if (this.id() !== payload.id) return;

                    this.contractRates35DForecast.set(payload);
                },
                ...handlerOpts,
            },
            setRateGraphRateType: {
                callback: (payload: RateTypePayload) => {
                    if (this.id() !== payload.id) return;

                    this.rateTypePayload.set(payload);
                },
                ...handlerOpts,
            },
            setRateGraphTripType: {
                callback: (payload: TripTypePayload) => {
                    if (this.id() !== payload.id) return;

                    this.tripTypePayload.set(payload);
                },
                ...handlerOpts,
            },
        });

        effect(() => {
            const data = this.historicalGraphData();
            this.clearRoot(this.rootId);
            this._graphBuilder.create13MonthHistoricalGraph(
                this.rootId(),
                this.graph(),
                data.spotData,
                data.contractData,
                [],
                ContractTypeEnum.Spot,
                {
                    average: data.bestFit.average,
                    high: data.bestFit.high,
                    low: data.bestFit.low,
                    escalation: 'SEVENDAYS',
                },
                true,
                true,
                'test',
            );
        });

        effect(() => {
            if (this.vizTime() === 'Hybrid') {
                const dataKey = '';
                /**
                 * This dispatches an event when the component clicks on a search
                 *
                 *
                 * To listen to these events, you can use the following format in your Dashboard, Section, or Component:
                 *
                 * ```json
                 * {
                 *  "onLoad": [
                 *    {
                 *      "name": "EVENT_NAME",
                 *      "payload": {},
                 *      "sourceEventRule": {
                 *          "payload": [
                 *            {
                 *                "path": "id",
                 *                "operator": "eq",
                 *                "value": "COMPONENT_ID"
                 *            },
                 *            {
                 *                "path": "componentName",
                 *                "operator": "eq",
                 *                "value": "IqGraphRatesHybrid"
                 *            },
                 *          ]
                 *      },
                 *      "rules": []
                 *    },
                 *   ],
                 * }
                 * ```
                 */
                const dispatch = () =>
                    this._eventBus.dispatch({
                        name: 'type:load',
                        payload: {
                            id: this.id(),
                            componentName: 'IqGraphRatesHybrid',
                            escalationOrigin:
                                this[dataKey]?.[0]?.escalationOrigin,
                            escalationDestination:
                                this[dataKey]?.[0]?.escalationDestination,
                        },
                    });

                dispatch();
            }
        });
        effect(async () => {
            const dataKey = 's';
            /**
             * This dispatches an event when the component clicks on a search
             *
             *
             * To listen to these events, you can use the following format in your Dashboard, Section, or Component:
             *
             * ```json
             * {
             *  "onLoad": [
             *    {
             *      "name": "EVENT_NAME",
             *      "payload": {},
             *      "sourceEventRule": {
             *          "payload": [
             *            {
             *                "path": "id",
             *                "operator": "eq",
             *                "value": "COMPONENT_ID"
             *            },
             *            {
             *                "path": "componentName",
             *                "operator": "eq",
             *                "value": "IqGraphRatesHybrid"
             *            },
             *          ]
             *      },
             *      "rules": []
             *    },
             *   ],
             * }
             * ```
             */
            const dispatch = () =>
                this._eventBus.dispatch({
                    name: 'type:load',
                    payload: {
                        id: this.id(),
                        componentName: 'IqGraphRatesHybrid',
                        escalationOrigin: this[dataKey]?.[0]?.escalationOrigin,
                        escalationDestination:
                            this[dataKey]?.[0]?.escalationDestination,
                    },
                });

            dispatch();
        });
    }

    browserOnly(f: () => void) {
        if (isPlatformBrowser(this.platformId)) {
            this.zone.runOutsideAngular(() => {
                f();
            });
        }
    }

    clearRoot(rootId) {
        am5.array.each(am5.registry.rootElements, function (r) {
            if (r?.dom?.id == rootId) {
                r.dispose();
            }
        });
    }

    ngOnDestroy() {
        // Clean up root element when the component is removed
        this.browserOnly(() => {
            const root = this.rootId();
            this.clearRoot(root);
        });

        this._eventBus.unregisterMap(this._dispatchActions);
    }
}
